'use client';

import { KeyRound, LoaderCircle, ShieldCheck, ShieldX, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

import { responseMessage, type WorkspaceSummary } from './app-shell';

type SupportGrant = Readonly<{
  grantId: string;
  operatorUserId: string;
  purpose: string;
  actionScopes: readonly string[];
  sharedConversationIds: readonly string[];
  fieldScopes: readonly string[];
  expiresAt: string;
  status: 'ACTIVE' | 'REVOKED' | 'EXPIRED';
}>;

const companyFieldScopes = [
  'company.name',
  'company.address',
  'company.contactName',
  'company.tel',
  'company.email',
  'company.defaultLanguage',
  'company.numberingPrefix',
] as const;

export function SupportAccessPanel({ role }: Readonly<{ role: WorkspaceSummary['role'] }>) {
  const [grants, setGrants] = useState<readonly SupportGrant[]>([]);
  const [loading, setLoading] = useState(role === 'OWNER');
  const [pending, setPending] = useState<'create' | 'revoke' | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<SupportGrant | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (role !== 'OWNER') return;
    try {
      const response = await fetch('/api/support-grants', { cache: 'no-store' });
      if (!response.ok) throw new Error(await responseMessage(response));
      const payload = await response.json() as { grants: readonly SupportGrant[] };
      setGrants(payload.grants);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '支持授权读取失败。');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  async function createGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const actionScopes = form.getAll('actionScopes').filter((value): value is string => typeof value === 'string');
    if (actionScopes.length === 0) {
      setError('至少选择一项支持权限。');
      return;
    }

    setPending('create');
    setError(null);
    setMessage(null);
    const durationHours = Number(form.get('durationHours'));
    const fieldScopes = [
      ...(actionScopes.includes('WRITE_COMPANY') ? companyFieldScopes : []),
      ...(actionScopes.includes('UPDATE_LOGO') ? ['logo'] : []),
    ];
    try {
      const response = await jsonMutation('/api/support-grants', {
        operatorUserId: form.get('operatorUserId'),
        purpose: form.get('purpose'),
        expiresAt: new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString(),
        actionScopes,
        sharedConversationIds: splitIdentifiers(form.get('sharedConversationIds')),
        fieldScopes,
        password: form.get('password'),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const created = await response.json() as SupportGrant;
      setGrants((current) => [created, ...current]);
      formElement.reset();
      setMessage('支持授权已创建，可随时提前撤销。');
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '支持授权创建失败。');
    } finally {
      setPending(null);
    }
  }

  async function revokeGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!revokeTarget) return;
    const form = new FormData(event.currentTarget);
    setPending('revoke');
    setError(null);
    setMessage(null);
    try {
      const response = await jsonMutation(`/api/support-grants/${encodeURIComponent(revokeTarget.grantId)}/revoke`, {
        reason: form.get('reason'),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const revoked = await response.json() as SupportGrant;
      setGrants((current) => current.map((grant) => grant.grantId === revoked.grantId ? revoked : grant));
      setRevokeTarget(null);
      setMessage('支持授权已撤销。');
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '支持授权撤销失败。');
    } finally {
      setPending(null);
    }
  }

  return <>
    <section className="settings-section" aria-labelledby="support-access-title">
      <div className="settings-section-heading">
        <ShieldCheck size={20} aria-hidden="true" />
        <div><h2 id="support-access-title">受限支持授权</h2><span>独立账号 · 最长 24 小时 · 全程审计</span></div>
      </div>

      {role !== 'OWNER' ? (
        <p className="settings-empty">只有 Owner 可以创建或撤销限时支持授权。</p>
      ) : <>
        <form className="settings-form support-grant-form" onSubmit={createGrant}>
          <fieldset disabled={pending !== null}>
            <div className="settings-fields settings-fields-two">
              <label>支持人员账号 ID<input name="operatorUserId" required autoComplete="off" /></label>
              <label>有效时长<select name="durationHours" defaultValue="2"><option value="1">1 小时</option><option value="2">2 小时</option><option value="4">4 小时</option><option value="8">8 小时</option><option value="24">24 小时</option></select></label>
              <label className="settings-field-wide">授权目的<textarea name="purpose" required minLength={12} maxLength={500} rows={3} /></label>
              <fieldset className="support-scope-fieldset settings-field-wide">
                <legend>允许的操作</legend>
                <div className="support-scope-grid">
                  <label className="settings-check"><input name="actionScopes" type="checkbox" value="READ_WORKSPACE" />查看工作区概况</label>
                  <label className="settings-check"><input name="actionScopes" type="checkbox" value="WRITE_COMPANY" />修改公司资料</label>
                  <label className="settings-check"><input name="actionScopes" type="checkbox" value="UPDATE_LOGO" />更新公司 Logo</label>
                  <label className="settings-check"><input name="actionScopes" type="checkbox" value="READ_SHARED_CONVERSATION" />查看指定共享会话</label>
                </div>
              </fieldset>
              <label className="settings-field-wide">已共享会话 ID（每行一个）<textarea name="sharedConversationIds" rows={2} placeholder="仅在选择查看共享会话时填写" /></label>
              <label className="settings-field-wide">Owner 当前密码<input name="password" type="password" required minLength={8} maxLength={128} autoComplete="current-password" /></label>
            </div>
            <button className="primary-button" type="submit"><KeyRound size={17} />{pending === 'create' ? '创建中' : '创建支持授权'}</button>
          </fieldset>
        </form>

        {loading ? <p className="settings-note"><LoaderCircle className="spin" size={15} />正在读取支持授权</p> : grants.length > 0 ? (
          <div className="support-grant-list" role="list">
            {grants.map((grant) => (
              <div className="support-grant-row" role="listitem" key={grant.grantId}>
                <div className="support-grant-main">
                  <div className="support-grant-title"><strong>{grant.operatorUserId}</strong><span className={`status-badge ${grant.status === 'ACTIVE' ? 'status-success' : 'status-muted'}`}>{supportStatusLabel(grant.status)}</span></div>
                  <span>{grant.purpose}</span>
                  <code>{grant.actionScopes.join(' · ')}</code>
                  <small>{formatDateTime(grant.expiresAt)} 到期</small>
                </div>
                {grant.status === 'ACTIVE' && <button className="secondary-button" type="button" onClick={() => setRevokeTarget(grant)}><ShieldX size={16} />撤销授权</button>}
              </div>
            ))}
          </div>
        ) : <p className="settings-empty">暂无支持授权。</p>}
      </>}

      {message && <p className="form-success" role="status">{message}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </section>

    {revokeTarget && <div className="modal-backdrop" role="presentation">
      <section className="modal-panel support-revoke-dialog" role="dialog" aria-modal="true" aria-labelledby="support-revoke-title">
        <div className="modal-heading"><div><p className="eyebrow">立即失效</p><h2 id="support-revoke-title">撤销支持授权</h2></div><button className="icon-button" type="button" aria-label="关闭" title="关闭" onClick={() => setRevokeTarget(null)}><X size={17} /></button></div>
        <p className="modal-copy">撤销后，支持人员的现有会话也不能继续使用这项授权。</p>
        <form className="settings-form" onSubmit={revokeGrant}>
          <fieldset disabled={pending !== null}>
            <div className="settings-fields"><label>撤销原因<textarea name="reason" required minLength={5} maxLength={300} rows={3} autoFocus /></label></div>
            <div className="modal-actions"><button className="secondary-button" type="button" onClick={() => setRevokeTarget(null)}>取消</button><button className="primary-button" type="submit"><ShieldX size={16} />{pending === 'revoke' ? '撤销中' : '确认撤销'}</button></div>
          </fieldset>
        </form>
      </section>
    </div>}
  </>;
}

async function jsonMutation(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'idempotency-key': crypto.randomUUID() },
    body: JSON.stringify(body),
  });
}

function splitIdentifiers(value: FormDataEntryValue | null): string[] {
  if (typeof value !== 'string') return [];
  return [...new Set(value.split(/[\s,]+/).map((item) => item.trim()).filter(Boolean))];
}

function supportStatusLabel(status: SupportGrant['status']): string {
  return status === 'ACTIVE' ? '授权中' : status === 'REVOKED' ? '已撤销' : '已过期';
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('zh-CN', { hour12: false });
}
